import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Flag } from "lucide-react";

/**
 * Usage: <ReportDialog targetType="listing" targetId={id} testId="report-listing" />
 */
export default function ReportDialog({ targetType, targetId, testId = "report-btn", label = "Report" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (reason.trim().length < 5) { toast.error("Give a bit more detail"); return; }
    setLoading(true);
    try {
      await api.post("/reports", { target_type: targetType, target_id: targetId, reason: reason.trim() });
      toast.success("Report submitted. An admin will review it.");
      setOpen(false); setReason("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to report");
    } finally { setLoading(false); }
  };

  const trigger = () => {
    if (!user) { nav("/login"); return; }
    setOpen(true);
  };

  return (
    <>
      <button
        onClick={trigger}
        data-testid={testId}
        className="text-xs overline text-muted-foreground hover:text-safety flex items-center gap-1 transition-colors duration-200"
      >
        <Flag className="w-3 h-3" /> {label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white rounded-none">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Report this {targetType}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Reports go to a human admin for review. Please describe the issue.</p>
          <Textarea
            rows={5}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Misleading photos, spam, prohibited item, hate speech…"
            data-testid={`${testId}-reason`}
            className="rounded-none"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
            <Button
              onClick={submit}
              disabled={loading}
              data-testid={`${testId}-submit`}
              className="rounded-none bg-safety hover:bg-safety-hover text-white"
            >
              {loading ? "Submitting…" : "Submit report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
